<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useToast } from "@/composables";
import {
  buildSelectedMappingsPayload,
  buildSingleSubTypeMappingPayload,
  buildSingleTypeMappingPayload,
  extractErrorMessage,
  fetchEntityTypesByParentId,
  fetchPrimaryEntityTypeOptions,
  fetchTypeMappings,
  formatSlugLabel,
  primarySelectionsFromSubMappings,
  saveTypeMappings,
  selectionsFromMappings,
  type EntityTypeOption,
} from "./nirTypeMappingsApi";

const { successToast, errorToast } = useToast();

const loading = ref(false);
const hasLoaded = ref(false);
const saving = ref(false);
const savingTypeKey = ref<string | null>(null);
const savingSubTypeKey = ref<string | null>(null);
const typeKeys = ref<string[]>([]);
const subTypeKeys = ref<string[]>([]);
const typeSelections = ref<Record<string, string>>({});
const subTypePrimarySelections = ref<Record<string, string>>({});
const subTypeSelections = ref<Record<string, string>>({});
const primaryOptions = ref<EntityTypeOption[]>([]);
const subOptionsByParentId = ref<Record<string, EntityTypeOption[]>>({});
const loadingSubOptionsFor = ref<Record<string, boolean>>({});

const mappedTypeCount = computed(() =>
  typeKeys.value.filter((key) => !!typeSelections.value[key]).length
);
const mappedSubTypeCount = computed(() =>
  subTypeKeys.value.filter(
    (key) => !!subTypePrimarySelections.value[key] && !!subTypeSelections.value[key]
  ).length
);

const hasSelectedMappings = computed(() => mappedTypeCount.value > 0 || mappedSubTypeCount.value > 0);

const isRowBusy = (typeKey?: string, subTypeKey?: string) =>
  saving.value ||
  (typeKey != null && savingTypeKey.value === typeKey) ||
  (subTypeKey != null && savingSubTypeKey.value === subTypeKey);

const buildPayloadArgs = () => [
  typeKeys.value,
  subTypeKeys.value,
  typeSelections.value,
  subTypePrimarySelections.value,
  subTypeSelections.value,
  primaryOptions.value,
  subOptionsByParentId.value,
] as const;

const submitPayload = async (payload: ReturnType<typeof buildSelectedMappingsPayload>, successMessage: string) => {
  const response = await saveTypeMappings(payload);
  successToast(
    "Success",
    (response.data as { message?: string })?.message || successMessage
  );
  await loadMappings();
};

const loadEntityTypeOptions = async () => {
  primaryOptions.value = await fetchPrimaryEntityTypeOptions();
};

const loadSubOptionsForParent = async (parentId: string) => {
  if (!parentId || subOptionsByParentId.value[parentId]) return;
  subOptionsByParentId.value[parentId] = await fetchEntityTypesByParentId(
    parentId,
    primaryOptions.value
  );
};

const loadMappings = async () => {
  const mappings = await fetchTypeMappings();
  typeKeys.value = Object.keys(mappings.types);
  subTypeKeys.value = Object.keys(mappings.subTypes);
  typeSelections.value = selectionsFromMappings(mappings.types);
  subTypePrimarySelections.value = primarySelectionsFromSubMappings(mappings.subTypes);
  subTypeSelections.value = selectionsFromMappings(mappings.subTypes);

  const parentIds = [
    ...new Set(
      Object.values(mappings.subTypes)
        .map((entry) => entry?.parentId)
        .filter((id): id is string => !!id)
    ),
  ];
  await Promise.all(parentIds.map((parentId) => loadSubOptionsForParent(parentId)));
};

const fetch = async () => {
  loading.value = true;
  try {
    await loadEntityTypeOptions();
    await loadMappings();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Failed to load type mappings"));
  } finally {
    loading.value = false;
    hasLoaded.value = true;
  }
};

const getSubOptionsForRow = (key: string) => {
  const parentId = subTypePrimarySelections.value[key];
  return parentId ? subOptionsByParentId.value[parentId] ?? [] : [];
};

const onSubTypePrimaryChange = async (key: string) => {
  subTypeSelections.value[key] = "";
  const parentId = subTypePrimarySelections.value[key];
  if (!parentId) return;

  loadingSubOptionsFor.value[key] = true;
  try {
    await loadSubOptionsForParent(parentId);
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Failed to load entity sub types"));
  } finally {
    loadingSubOptionsFor.value[key] = false;
  }
};

const save = async () => {
  const payload = buildSelectedMappingsPayload(...buildPayloadArgs());
  if (Object.keys(payload.types).length === 0 && Object.keys(payload.subTypes).length === 0) {
    errorToast("Error", "Select at least one mapping to save");
    return;
  }

  saving.value = true;
  try {
    await submitPayload(payload, "Type mappings saved successfully");
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Failed to save type mappings"));
  } finally {
    saving.value = false;
  }
};

const saveTypeRow = async (key: string) => {
  const payload = buildSingleTypeMappingPayload(key, ...buildPayloadArgs());
  if (Object.keys(payload.types).length === 0) {
    errorToast("Error", "Select a NIR entity type before saving");
    return;
  }

  savingTypeKey.value = key;
  try {
    await submitPayload(payload, `${formatSlugLabel(key)} mapping saved successfully`);
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Failed to save entity type mapping"));
  } finally {
    savingTypeKey.value = null;
  }
};

const saveSubTypeRow = async (key: string) => {
  const payload = buildSingleSubTypeMappingPayload(key, ...buildPayloadArgs());
  if (Object.keys(payload.subTypes).length === 0) {
    errorToast("Error", "Select a primary and sub type before saving");
    return;
  }

  savingSubTypeKey.value = key;
  try {
    await submitPayload(payload, `${formatSlugLabel(key)} mapping saved successfully`);
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Failed to save entity sub type mapping"));
  } finally {
    savingSubTypeKey.value = null;
  }
};

onMounted(() => fetch());

defineExpose({ refresh: fetch });
</script>

<template>
  <div class="flex-1 flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div>
        <h2 class="text-base font-semibold text-gray-800">Mappings</h2>
        <p class="text-sm text-gray-500 mt-1">
          Map eRegistry entity keys to NIR entity types. Save each row individually or save all selected mappings at once.
        </p>
      </div>
      <div class="flex gap-2">
        <button type="button" class="btn shrink-0" :disabled="loading || saving" @click="fetch">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
        <button
          type="button"
          class="btn-primary"
          :disabled="loading || saving || !hasSelectedMappings"
          @click="save"
        >
          <i class="fa fa-save mr-1" :class="{ 'fa-spin': saving }"></i>
          {{ saving ? "Saving..." : "Save Selected" }}
        </button>
      </div>
    </div>

    <div class="relative min-h-[320px]">
      <div
        v-if="loading && !hasLoaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading mappings...</p>
      </div>

      <div class="flex flex-col gap-6" :class="{ 'invisible': loading && !hasLoaded }">
      <section class="bg-white rounded border border-gray-200 overflow-hidden w-full">
        <div class="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 class="text-sm font-semibold text-gray-800">Entity Types</h3>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ mappedTypeCount }} of {{ typeKeys.length }} mapped
          </p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full table-fixed text-xs border-collapse">
            <colgroup>
              <col style="width: 30%" />
              <col style="width: 58%" />
              <col style="width: 12%" />
            </colgroup>
            <thead class="bg-white">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Key</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">NIR Entity Type</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="key in typeKeys" :key="key" class="hover:bg-gray-50">
              <td class="py-3 px-4 border-b border-gray-100 align-middle">
                <p class="font-medium text-gray-800 leading-snug break-words">{{ formatSlugLabel(key) }}</p>
              </td>
              <td class="py-3 px-4 border-b border-gray-100 align-middle">
                <select
                  v-model="typeSelections[key]"
                  class="w-full px-2 py-2 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                  :disabled="loading || isRowBusy(key)"
                >
                  <option value="">Not mapped</option>
                  <option v-for="option in primaryOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </td>
              <td class="py-3 px-4 border-b border-gray-100 align-middle">
                <div class="flex items-center justify-center">
                  <button
                    type="button"
                    class="btn-sm-primary whitespace-nowrap"
                    title="Save mapping"
                    :disabled="loading || !typeSelections[key] || isRowBusy(key)"
                    @click="saveTypeRow(key)"
                  >
                    <i
                      class="fa-solid mr-1"
                      :class="savingTypeKey === key ? 'fa-spinner fa-spin' : 'fa-save'"
                    ></i>
                    Save
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="hasLoaded && !loading && typeKeys.length === 0">
              <td colspan="3" class="py-8 text-center text-gray-400">No entity type keys found.</td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bg-white rounded border border-gray-200 overflow-hidden w-full">
        <div class="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 class="text-sm font-semibold text-gray-800">Entity Sub Types</h3>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ mappedSubTypeCount }} of {{ subTypeKeys.length }} mapped
          </p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full table-fixed text-xs border-collapse">
            <colgroup>
              <col style="width: 26%" />
              <col style="width: 31%" />
              <col style="width: 31%" />
              <col style="width: 12%" />
            </colgroup>
            <thead class="bg-white">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Key</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Primary Type</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Sub Type</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="key in subTypeKeys" :key="key" class="hover:bg-gray-50">
              <td class="py-3 px-4 border-b border-gray-100 align-middle">
                <p class="font-medium text-gray-800 leading-snug break-words">{{ formatSlugLabel(key) }}</p>
              </td>
              <td class="py-3 px-4 border-b border-gray-100 align-middle">
                <select
                  v-model="subTypePrimarySelections[key]"
                  class="w-full px-2 py-2 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                  :disabled="loading || isRowBusy(undefined, key)"
                  @change="onSubTypePrimaryChange(key)"
                >
                  <option value="">Select primary</option>
                  <option v-for="option in primaryOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </td>
              <td class="py-3 px-4 border-b border-gray-100 align-middle">
                <select
                  v-model="subTypeSelections[key]"
                  class="w-full px-2 py-2 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                  :disabled="loading || !subTypePrimarySelections[key] || loadingSubOptionsFor[key] || isRowBusy(undefined, key)"
                >
                  <option value="">
                    {{
                      !subTypePrimarySelections[key]
                        ? "Select primary first"
                        : loadingSubOptionsFor[key]
                          ? "Loading..."
                          : "Not mapped"
                    }}
                  </option>
                  <option
                    v-for="option in getSubOptionsForRow(key)"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </td>
              <td class="py-3 px-4 border-b border-gray-100 align-middle">
                <div class="flex items-center justify-center">
                  <button
                    type="button"
                    class="btn-sm-primary whitespace-nowrap"
                    title="Save mapping"
                    :disabled="loading || !subTypePrimarySelections[key] || !subTypeSelections[key] || isRowBusy(undefined, key)"
                    @click="saveSubTypeRow(key)"
                  >
                    <i
                      class="fa-solid mr-1"
                      :class="savingSubTypeKey === key ? 'fa-spinner fa-spin' : 'fa-save'"
                    ></i>
                    Save
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="hasLoaded && !loading && subTypeKeys.length === 0">
              <td colspan="4" class="py-8 text-center text-gray-400">No entity sub type keys found.</td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>
      </div>
    </div>
  </div>
</template>
